import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { ArrowDropDown } from "@mui/icons-material";
import React, { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import { IIconsBaseProps, UserIcon } from "../../components/Primitives";
import { useSidebarStyle } from "../../style/sidebar.style";
import { AvailableMenus, isAvailableMenuValue } from "../../utils/sidebar.utils";
import { INavigationItem } from "../navigation/navigation-item";
import { useSidebarContext } from "./sidebar-context";
import { SubMenuItem } from "./submenu-item.component";

const useStyles = makeStyles()(() => ({
  detailsContainer: {
    backgroundColor: "transparent",
    paddingTop: 0,
    margin: 0,
    paddingLeft: 0,
    paddingBottom: 0,
    paddingRight: 0,
  },
}));

interface ISidebarMenuItemProps {
  name: string;
  navItem: INavigationItem;
  menuItem: string; // label of navItem
}

export const SidebarMenuItem: React.VFC<ISidebarMenuItemProps> = (props) => {
  const { classes } = useSidebarStyle();
  const { classes: containerClasses } = useStyles();
  const history = useHistory();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { setSidebarOpen, isFixed, activeMenu, setActiveMenu } = useSidebarContext();
  const { name, navItem, menuItem } = props;
  const NavIcon = navItem.icon ? (navItem.icon as FunctionComponent<IIconsBaseProps>) : UserIcon;

  // Determine if this accordion is expanded based on global state
  const isMenuExpandedLocal = isAvailableMenuValue(menuItem) && activeMenu === menuItem;

  // Handle user was redirected to menu item, and did not click on sidebar item
  useEffect(() => {
    if (!navItem.isParent || !navItem.content || !isAvailableMenuValue(navItem.label)) return;
    const isChildSelected =
      (navItem.content ?? []).find((child) => child.to && child.to !== "" && new RegExp(child.escapedUrl ?? child.to).test(pathname)) !== undefined;

    if (isChildSelected) {
      setActiveMenu(navItem.label);
    }
  }, [pathname, navItem.content, navItem.isParent, navItem.label, setActiveMenu]);

  const handleNavigationItemClick = (targetItem: INavigationItem): void => {
    if (targetItem.externalUrl) {
      window.open(targetItem.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    history.push(targetItem.to);
    if (!isFixed) {
      setSidebarOpen(false);
    }
  };

  const isActiveSubItem = (item: INavigationItem) => {
    const defaultIsVisible = item.to === pathname;
    const invisibleChildActive = (item.invisibleChildrenUrls ?? []).some((childUrl) =>
      new RegExp(childUrl).test(pathname),
    );
    return defaultIsVisible || invisibleChildActive;
  };

  const isActiveMenu = () => {
    const paths = navItem.content?.filter((item) => item.to && item.to !== "" && new RegExp(item.to).test(pathname));
    return paths && paths.length > 0 ? true : false;
  };

  const handleAccordion = () => {
    if (isMenuExpandedLocal) {
      setActiveMenu(AvailableMenus.None);
    } else if (isAvailableMenuValue(menuItem)) {
      setActiveMenu(menuItem);
    }
  };

  return (
    <Accordion
      expanded={isMenuExpandedLocal}
      onChange={handleAccordion}
      className={classes.expandListItem}
      id={`${name}-menu`}
      key={`${name}-menu`}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        key={`${name}-summary`}
        expandIcon={
          navItem.content && navItem.content.length > 0
            ? <ArrowDropDown color="primary" className={classes.expandIcon} />
            : undefined
        }
        aria-controls={`${name}-content`}
        id={`${name}-header`}
        className={classes.summary}
      >
        <ListItemIcon className={classes.listItemIcon}>
          <NavIcon svgColor="green" />
        </ListItemIcon>
        <ListItemText
          primary={t(navItem.label)}
          className={isActiveMenu() ? classes.activeListTitle : classes.listTitle}
        />
      </AccordionSummary>
      <AccordionDetails key={`${name}-details`} classes={{ root: containerClasses.detailsContainer }}>
        <List className={classes.list}>
          {(navItem.content ?? [])
            .filter((item) => item.visible)
            .map((item, index) => {
              return (
                <SubMenuItem
                  key={`${item.label}-${index}`}
                  onClick={() => handleNavigationItemClick(item)}
                  label={item.label}
                  isActive={isActiveSubItem(item)}
                />
              );
            })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
